import { useState } from 'react';
import { C, soft } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { type } from '@parsina/framework/clinical/src/constants/typography';
import { useGiftCodes } from '../../hooks/useGiftCodes';
import { notifyWalletChanged } from '@parsina/framework/clinical/src/hooks/useWalletBalance';

/**
 * Redeem a campaign gift code, and see the ones already redeemed.
 *
 * The code input is FORCED LTR with `dir="ltr"` even though the panel is RTL:
 * the code is a Latin token with a meaningful left-to-right order, and in an
 * RTL input a leading '-' or a mixed digit run reorders on screen while the
 * value stays correct — so the doctor proofreads a string that is not the one
 * being sent.
 *
 * Persian digits are NOT converted here. The marketing service normalises them
 * (app/domain/gift_codes.py:normalize_code), so a doctor typing on a Persian
 * keyboard is already handled, and a second normaliser in the browser would be
 * a second place for the rule to drift.
 */

// Teal ink darkened from the live token so it keeps text contrast and still
// follows the active preset (the old hand-picked dark teal under the default preset).
const TEAL_INK = `color-mix(in srgb, ${C.teal} 70%, #000)`;

const STATUS_TONE = {
  issued:   { color: TEAL_INK,  bg: C.tealLight },
  redeemed: { color: C.navy,    bg: soft(C.navy, 0.1) },
  revoked:  { color: C.textMuted, bg: C.bg },
  expired:  { color: C.textMuted, bg: C.bg },
};

function CheckIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function GiftIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 12v8H4v-8" /><path d="M22 7H2v5h20z" /><path d="M12 7v13" />
      <path d="M12 7c-1.5 0-4-.6-4-3a2 2 0 0 1 4 0" />
      <path d="M12 7c1.5 0 4-.6 4-3a2 2 0 0 0-4 0" />
    </svg>
  );
}

export default function GiftCodeCard({ doctorId, onRedeemed, compact = false }) {
  const { t, formatNumber } = useTranslation();
  const { codes, redeem } = useGiftCodes(doctorId);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (busy) return;
    setBusy(true);
    const result = await redeem(value);
    setBusy(false);
    if (result.ok) {
      setDone(result);
      setValue('');
      // The balance in the page header is now stale — the hero and the KPI
      // strip both read it, so every mounted copy refetches rather than this
      // card holding a second copy of the number.
      notifyWalletChanged();
      if (onRedeemed) onRedeemed(result);
      return;
    }
    setError(
      result.code === 'EMPTY' ? t('credits.gift.empty')
        : result.message || t('credits.gift.notFound'),
    );
  }

  if (done) {
    return (
      <div data-testid="gift-success" style={{
        background: '#fff', border: `1.5px solid ${C.green}`, borderRadius: 12,
        padding: 18, display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{
            width: 38, height: 38, borderRadius: '50%', background: C.green, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}><CheckIcon /></span>
          <div>
            <div style={{ ...type.body, fontWeight: 800, color: C.textPrimary }}>
              {t('credits.gift.success', { parseh: formatNumber(done.granted) })}
            </div>
            <div style={{ ...type.caption, color: C.textSecondary, marginTop: 3 }}>
              {t('credits.gift.successSub', { visits: formatNumber(done.smartVisits) })}
            </div>
          </div>
        </div>
        {done.balance != null && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ ...type.caption, color: C.textSecondary }}>{t('credits.gift.newBalance')}</span>
            <span style={{ ...type.body, fontWeight: 800, color: TEAL_INK }} dir="ltr">
              {formatNumber(done.balance)}
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{
      background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12,
      padding: 18, display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{
          width: 34, height: 34, borderRadius: 10, background: soft(C.navy, 0.1), color: C.navy,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}><GiftIcon /></span>
        <div style={{ ...type.title, color: C.textPrimary }}>{t('credits.gift.title')}</div>
      </div>

      {!compact && (
        <div style={{ ...type.caption, color: C.textSecondary, lineHeight: 1.9 }}>
          {t('credits.gift.hint')}
        </div>
      )}

      {/* STACKED, not a row. This card sits in a ~240px column on the web
          credits screen, and a side-by-side input+button cannot fit there: a
          flex item has `min-width: auto`, so the input refuses to shrink below
          the intrinsic width of its placeholder (~193px) and pushes the button
          clean out of the card — it rendered at x=-6, outside its own parent.
          Stacking fits every width, and gives the button a full-width target
          rather than a ~94px sliver. */}
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input
          dir="ltr"
          value={value}
          onChange={(e) => { setValue(e.target.value); if (error) setError(''); }}
          placeholder={t('credits.gift.placeholder')}
          data-testid="gift-code-input"
          aria-label={t('credits.gift.title')}
          aria-invalid={!!error}
          style={{
            width: '100%', minWidth: 0, height: 46, borderRadius: 10,
            boxSizing: 'border-box',
            border: `1px solid ${error ? C.red : C.border}`,
            background: C.bg, padding: '0 12px', fontFamily: 'inherit',
            fontSize: 15, fontWeight: 700, letterSpacing: 1.5,
            color: C.textPrimary, textAlign: 'center', outline: 'none',
          }}
        />
        <button
          type="submit" disabled={busy} data-testid="gift-code-submit"
          style={{
            width: '100%', height: 46, padding: '0 20px', border: 'none',
            borderRadius: 10,
            background: C.navy, color: '#fff', fontFamily: 'inherit',
            ...type.body, fontWeight: 700, cursor: busy ? 'default' : 'pointer',
            opacity: busy ? 0.6 : 1, whiteSpace: 'nowrap',
          }}>
          {busy ? t('credits.gift.submitting') : t('credits.gift.submit')}
        </button>
      </form>

      {error && (
        <div role="alert" data-testid="gift-code-error" style={{ ...type.caption, color: C.red, lineHeight: 1.8 }}>
          {error}
        </div>
      )}

      {!error && !compact && (
        <div style={{ ...type.caption, color: C.textMuted }}>{t('credits.gift.oneUse')}</div>
      )}

      {codes.length > 0 && (
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ ...type.caption, fontWeight: 700, color: C.textSecondary }}>
            {t('credits.gift.myCodes')}
          </div>
          {codes.map((g) => {
            const tone = STATUS_TONE[g.status] || STATUS_TONE.revoked;
            return (
              <div key={g.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <span dir="ltr" style={{ ...type.caption, fontWeight: 800, color: C.navyDark || C.navy, letterSpacing: 1 }}>
                    {g.code}
                  </span>
                  <span style={{ ...type.caption, color: C.textMuted }}>
                    {t('credits.gift.worth', {
                      parseh: formatNumber(g.parseh), visits: formatNumber(g.smartVisits),
                    })}
                  </span>
                </div>
                <span style={{
                  ...type.caption, fontWeight: 700, color: tone.color, background: tone.bg,
                  borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap',
                }}>
                  {t(`credits.gift.status.${g.status}`)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
